/**
 * Presentation of the few raw (non-envelope) numbers the API hands the client: storage sizes in
 * the Settings read-only block (§5.1 #20), member/run counters and word counts.
 *
 * These are not derived analytics — no `Explained` ever passes through here (§12.5 rule 1);
 * calculated figures always arrive pre-formatted and are rendered by `<Num>`.
 */

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const;

/** "12.4 MB" — byte counts from `SettingsReadOnlyDTO`. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < BYTE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 ? value.toFixed(0) : value.toFixed(1)} ${BYTE_UNITS[unit]}`;
}

const integerFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

/** Thousands separators for plain counters (member counts, word counts, row totals). */
export function formatInteger(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return integerFormatter.format(value);
}

const decimalFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

/**
 * Baseline medians and chart-axis ticks: raw numbers the server sends without a display string
 * (they are inputs to a tooltip or an axis, not headline figures).
 */
export function formatDecimal(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return decimalFormatter.format(value);
}

/** "1 profile" / "3 profiles". */
export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${formatInteger(count)} ${count === 1 ? singular : plural}`;
}

/** Collapses whitespace and hard-truncates — used for menu labels and dialog titles only. */
export function truncate(text: string, max: number): string {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  return collapsed.length <= max ? collapsed : `${collapsed.slice(0, max - 1)}…`;
}
